import React, { Fragment, useState, useRef } from "react";
import { Listbox, Transition, Portal } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon as ChevronUpDownIcon } from '@heroicons/react/outline'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

/**
 * @param {*} SearchableSelect 
 */
export const SearchableSelect = ({ label, name, value, options, onChange, disabled, error }) => {
  const hasLabel = !!label;

  const defaultValue = options.filter(x => { return x.value === value })[0];
  const [selected, setSelected] = useState(defaultValue);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef();
  const inputRef = useRef();

  const updatePosition = (open) => {
    const rect = buttonRef.current.getBoundingClientRect()
    setCoords({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    })
    if (!open) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  const onListBoxChange = (obj) => {
    setSelected(obj);
    value = obj.value;
    onChange({ target: { name: name, value: obj.value } });
  }

  const getSelectedName = () => {
    return selected?.name || defaultValue?.name || "\ufeff";
  }

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
        option.value === "" ||
        option.name.toLowerCase().includes(query.toLowerCase())
      )

  return (
    <>
      <div className={classNames(hasLabel ? "space-y-1 sm:space-y-0 sm:py-1" : "", "sm:grid sm:grid-cols-1 sm:gap-3")}>
        {!hasLabel &&
          <div>
            <label
              htmlFor="project_name"
              className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2 dark:text-gray-300"
            >
              {label}
            </label>
          </div>
        }
        <div className="sm:col-span-2">
          <Listbox value={selected} name={name} onChange={onListBoxChange} disabled={disabled}>
            {({ open }) => (
              <>
                <Listbox.Label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-300">{label}</Listbox.Label>
                <div className="relative mt-2">
                  <Listbox.Button
                    ref={buttonRef}
                    className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    onClick={() => updatePosition(open)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp") {
                        updatePosition(open);
                      }
                    }}
                  >
                    <span className="block truncate">{getSelectedName()}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </Listbox.Button>

                  {open && coords && (
                    <Portal>
                      <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options
                          static
                          className="fixed z-50 mt-1 w-full rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                          style={{
                            top: coords.top,
                            left: coords.left,
                            width: coords.width,
                          }}
                        >
                          {/* Search input */}
                          <div className="p-2">
                            <input
                              id={`${name}-search`}
                              ref={inputRef}
                              type="text"
                              placeholder="Search..."
                              value={query}
                              onChange={(e) => setQuery(e.target.value)}
                              className="w-full rounded-md ring-1 ring-inset ring-gray-300 px-3 py-1.5 text-gray-900 sm:text-sm"
                            />
                          </div>
                          <div className="overflow-auto max-h-60">
                            {filteredOptions?.map((option) => (
                              <Listbox.Option
                                key={`${option.name}${option.value}`}
                                disabled={option.disabled}
                                className={({ active }) =>
                                  classNames(
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                                    'relative cursor-default select-none py-2 pl-3 pr-9'
                                  )
                                }
                                value={option}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                      {option.name}
                                    </span>

                                    {selected ? (
                                      <span
                                        className={classNames(
                                          active ? 'text-white' : 'text-indigo-600',
                                          'absolute inset-y-0 right-0 flex items-center pr-4'
                                        )}
                                      >
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </div>
                        </Listbox.Options>
                      </Transition>
                    </Portal>
                  )}
                </div>
              </>
            )}
          </Listbox>
          {
            error &&
            <p className="mt-2 text-sm text-red-600" id="email-error">
              {error}
            </p>
          }
        </div>
      </div>
    </>
  );
}